/**
 * Self-mounts `Component` into every `[data-frontend="<name>"]` container:
 * parses `data-props` as JSON, mounts idempotently (skips containers already
 * marked `data-frontend-mounted`), and re-runs on `turbo:load`. React/ReactDOM
 * are read from `window` (the host page provides them as externals).
 *
 * Turbo: the bundle IIFE re-executes on every visit, so listeners register once
 * per name (via a `window` registry) to avoid accumulating a listener/scan per
 * navigation; on `turbo:before-cache` the roots are unmounted and their markers
 * cleared, so a restored body clone (which has no React roots/listeners)
 * remounts fresh instead of showing an inert, marked-but-dead UI.
 */
export function mountFrontend(name, Component) {
    const w = window;
    const roots = (w.__frontendRoots ?? (w.__frontendRoots = new Map()));
    const mount = () => {
        if (!w.React || !w.ReactDOM)
            return;
        document
            .querySelectorAll(`[data-frontend="${name}"]`)
            .forEach(el => {
            if (el.dataset.frontendMounted)
                return;
            let props = {};
            try {
                props = JSON.parse(el.dataset.props || '{}');
            }
            catch (e) {
                console.error(`Failed to parse data-props for ${name}`, e);
            }
            // Mark only after a successful render, so a throw stays retryable.
            try {
                const root = w.ReactDOM.createRoot(el);
                root.render(w.React.createElement(Component, props));
                roots.set(el, root);
                el.dataset.frontendMounted = 'true';
            }
            catch (e) {
                console.error(`Failed to mount frontend "${name}"`, e);
            }
        });
    };
    const beforeCache = () => {
        roots.forEach((root, el) => {
            try {
                root.unmount();
            }
            catch {
                /* container already gone */
            }
            delete el.dataset.frontendMounted;
        });
        roots.clear();
    };
    const seen = (w.__frontendMountListeners ?? (w.__frontendMountListeners = new Set()));
    if (!seen.has(name)) {
        seen.add(name);
        document.addEventListener('DOMContentLoaded', mount);
        document.addEventListener('turbo:load', mount);
        document.addEventListener('turbo:before-cache', beforeCache);
    }
    // Always mount now: a re-executed bundle must mount the fresh container even
    // though its listeners are already registered.
    if (document.readyState !== 'loading')
        mount();
}
